import {useRef, useState} from 'react';
import {
    Alert,
    Button,
    Group,
    SimpleGrid,
    Stack,
    Text,
    Tooltip,
} from '@mantine/core';
import {I18n} from 'aws-amplify/utils';
import {DataTableColumn} from 'mantine-datatable';

import {
    DataTableTokenPagination,
    FlexiDeleteButton,
    FlexiEditButton,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';

import {useDnsRecords} from '../hooks/useDnsRecords';
import {useDnsRecordsSearch} from '../hooks/useDnsRecordsSearch';
import {useDnsRecordsSelection} from '../hooks/useDnsRecordsSelection';
import {useDnsRecordsFilter} from '../hooks/useDnsRecordsQuery';
import {useDnsRecordsTableColumns} from '../hooks/useDnsRecordsTableColumns';
import {useRecordWriteCapabilities} from '../hooks/useRecordWriteCapabilities';
import {useRecordDeletion} from '../hooks/useRecordDeletion';
import {DeleteRecordModal} from '../components/DeleteRecordModal';
import {RecordFormDrawer} from '../drawers/RecordFormDrawer';
import {
    DnsRecord,
    isDeletableRecord,
    isEditableAsRdata,
    recordKey,
} from '../types';

// ⚠️ NAMED DnsRecordsTable, NOT RecordsTable.
//
// THREE modules in this app already carry a `RecordsTable.tsx` — domain-names,
// registrars and top-level-domains — and NONE of them renders DNS records.
// They are generic scaffold names over their own module's entity. This module
// is the one that genuinely renders records, so its table takes the specific
// name rather than adding a fourth ambiguous one.
export const DnsRecordsTable = (
    {
        connectionId,
        zoneId,
        zoneName,
    }: {
        connectionId?: string,
        zoneId?: string,
        zoneName?: string,
    },
) => {

    const {columns} = useDnsRecordsTableColumns();

    const {
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchRecords,
        reload,
        isReloading,
    } = useDnsRecords(connectionId, zoneId);

    const {
        keyword,
        handleFilterByKeyword,
    } = useDnsRecordsSearch();

    const {filters} = useDnsRecordsFilter();

    const {
        selectedRecords,
        setSelectedRecords,
    } = useDnsRecordsSelection();

    const {canWriteRecords} = useRecordWriteCapabilities(connectionId);

    const deletion = useRecordDeletion(connectionId, zoneId);

    // The record currently open for editing. Local state: only this table opens
    // the edit drawer, so there is nothing to share.
    const [editing, setEditing] = useState<DnsRecord | undefined>(undefined);

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const searching = keyword.trim() !== '';
    const filtering = Object.keys(filters).length > 0;

    // ⚠️ THE EMPTY STATES MEAN DIFFERENT THINGS and `noRecordsText` is one
    // string, so the distinction is branched here rather than flattened into a
    // single message that sends an operator hunting for a bug that is not there.
    //
    // In particular a FILTERED empty result is not "this zone is empty": with a
    // server-side type filter it means the provider holds no record of that
    // type, which is a fact about the filter and not about the zone.
    let noRecordsText = I18n.get('This zone has no records');
    if (searching && filtering) {
        noRecordsText = I18n.get('No loaded record of this type matches this search');
    } else if (searching) {
        noRecordsText = I18n.get('No loaded record matches this search');
    } else if (filtering) {
        noRecordsText = filters.type === 'NS'
            // The delegation case gets its own sentence, because "no NS
            // records" is a meaningful finding rather than an empty list: it
            // says this zone delegates nothing.
            ? I18n.get('This zone publishes no nameserver records, so it delegates no subdomain.')
            : I18n.get('This zone has no records of that type');
    }

    // ⚠️ ROW ACTIONS ARE A COLUMN THAT ONLY EXISTS WHEN WRITES ARE POSSIBLE.
    //
    // Appended rather than declared in useDnsRecordsTableColumns, because
    // whether it exists at all is a capability decision and the columns hook
    // has no business knowing about mutations.
    const actionsColumn: DataTableColumn<DnsRecord> = {
        accessor: 'actions',
        title: I18n.get('Actions'),
        textAlign: 'right',
        render: (item: DnsRecord) => (
            <Group gap={'xs'} justify={'flex-end'} wrap={'nowrap'}>
                {/* ⚠️ A POINTER CANNOT BE EDITED AS RDATA. Its `values` holds
                    the RESOLVED TARGET, so pre-filling an edit form from it
                    would offer today's resolved address as the stored value —
                    and saving that would silently convert an alias into a
                    literal record. The control is disabled with the reason
                    given rather than absent, because absent would read as a
                    permission problem. */}
                {isEditableAsRdata(item) ? (
                    <FlexiEditButton
                        size={'compact-xs'}
                        variant={'subtle'}
                        onClick={() => setEditing(item)}
                    >
                        {I18n.get('Edit')}
                    </FlexiEditButton>
                ) : (
                    <Tooltip
                        multiline={true}
                        w={260}
                        label={I18n.get('This record points at another resource. Its value is the resolved target rather than stored data, so it cannot be edited here — change it at the provider.')}
                    >
                        {/* A span wrapper: a disabled button does not fire the
                            events a Tooltip needs. */}
                        <span>
                            <FlexiEditButton size={'compact-xs'} variant={'subtle'} disabled={true}>
                                {I18n.get('Edit')}
                            </FlexiEditButton>
                        </span>
                    </Tooltip>
                )}
                {/* ⚠️ A POINTER CANNOT BE DELETED THROUGH THIS PATH EITHER,
                    and the reason is worse than the edit case (FNP-1279). The
                    provider matches a delete on the record's FULL alias
                    target, which the neutral record does not carry — so the
                    delete is either refused, or matches a DIFFERENT literal
                    record of the same name and type and removes that one.
                    Disabled with the reason given, matching Edit above:
                    absent would read as a permission problem, and the record
                    plainly exists. */}
                {isDeletableRecord(item) ? (
                    <FlexiDeleteButton
                        size={'compact-xs'}
                        variant={'subtle'}
                        onClick={() => deletion.requestDelete(item)}
                    >
                        {I18n.get('Delete')}
                    </FlexiDeleteButton>
                ) : (
                    <Tooltip
                        multiline={true}
                        w={260}
                        label={I18n.get('This record points at another resource. Deleting it needs the full alias target, which Flexi does not hold, so it must be removed at the provider.')}
                    >
                        {/* A span wrapper: a disabled button does not fire the
                            events a Tooltip needs. */}
                        <span>
                            <FlexiDeleteButton size={'compact-xs'} variant={'subtle'} disabled={true}>
                                {I18n.get('Delete')}
                            </FlexiDeleteButton>
                        </span>
                    </Tooltip>
                )}
            </Group>
        ),
    };

    const tableColumns = canWriteRecords
        ? [...columns, actionsColumn]
        : columns;

    return (
        <SimpleGrid verticalSpacing={'xl'} m={'sm'}>

            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {/* ⚠️ DISTINCT FROM AN EMPTY TABLE: this is the QUERY failing, so
                nothing at all is known — not "this zone has no records". */}
            {isError && (
                <Alert color={'red'} title={I18n.get('Could not load records')}>
                    <Group justify={'space-between'} align={'center'}>
                        {I18n.get('The domains API did not return this zone\'s records. This is not a provider problem — the request itself failed.')}
                        <Button
                            variant={'outline'}
                            size={'xs'}
                            onClick={reload}
                            loading={isReloading}
                        >
                            {I18n.get('Retry')}
                        </Button>
                    </Group>
                </Alert>
            )}

            {/* The NS filter makes the delegation visible, and a banner says so
                while it is on — otherwise a short list of NS records looks like
                a zone that has almost nothing in it. */}
            {filters.type === 'NS' && visibleItems.length > 0 && (
                <Alert color={'grape'} variant={'light'} title={I18n.get('Showing the delegation')}>
                    <Stack gap={4}>
                        <Text size={'sm'}>
                            {I18n.get('These nameserver records are what this zone publishes as its delegation. A subdomain listed here is served by the nameservers beside it, not by this zone.')}
                        </Text>
                    </Stack>
                </Alert>
            )}

            <MantineDataTable<DnsRecord>
                columns={tableColumns}
                records={visibleItems}
                // ⚠️ A COMPOSED KEY, because a record has NO id. Its identity is
                // (name, type) within the zone. This key is local to the
                // browser and is never sent to the API.
                idAccessor={(record: DnsRecord) => recordKey(record)}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                // Client-side sorting over the pages loaded so far — the same
                // scope the keyword works on. Token pagination cannot reorder
                // rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                // ⚠️ THE LOADING STATE, distinct from empty and from error.
                fetching={loading}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                // Passing a term is what selects the filtered footer state —
                // omit it and a narrowed view silently reports the unfiltered
                // one.
                filterTerm={searching ? keyword : (filtering ? 'filtered' : undefined)}
                noun={'record'}
                nextToken={nextToken}
                isFetchingMore={isFetchingNextPage}
                handleLoadMore={fetchRecords}
            />

            <DeleteRecordModal
                record={deletion.record}
                zoneName={zoneName}
                opened={deletion.opened}
                onClose={deletion.cancelDelete}
                onConfirm={deletion.confirmDelete}
                isDeleting={deletion.isDeleting}
                error={deletion.error}
            />

            <RecordFormDrawer
                connectionId={connectionId}
                zoneId={zoneId}
                record={editing}
                opened={Boolean(editing)}
                onClose={() => setEditing(undefined)}
            />

        </SimpleGrid>
    );
};
